<template>
    <div class="" v-if="componentToShow === 'ProductDetail'">
        <!-- Product Detail Page -->
        <div class="flex flex-col md:flex-row gap-8">
            <div class="flex shrink-0 md:w-[350px] md:h-[350px] relative">
                <img id="global-catalog-product-image" :src="productImageUrl" @error="getMediaUrl('error')" class="w-full h-full" />
                <img :src="fortifiedIconImage" v-if="hasFortifiedCertification" class="absolute top-0 left-0 h-[80px]" width="80px" />
            </div>
            <div class="flex flex-col gap-2 w-full">
                <div class="font-normal text-base text-[#464646]">{{ product.manufacturer }}</div>
                <div class="font-semibold text-[26px] text-[#171717]">{{ product.webName }}</div>
                <div class="font-normal text-base text-[#464646]">{{selectedVariant?.agilitySize}}</div>
                <div class="font-normal text-base text-[#464646]">
                    {{ $t('catalog.itemCode') }} {{ selectedVariant?.itemCode }}
                    <span class="" v-if="selectedVariant?.manufacturerPartNumber">
                        | {{ $t('catalog.mfg') }} {{ selectedVariant?.manufacturerPartNumber }}
                    </span>
                </div>
                <div class="">
                    <div v-if="isVariantNotAvailableAtBranch" class="flex gap-1 items-start">
                        <i class="ri-error-warning-fill text-red-600 text-2xl flex-shrink-0"></i>
                        <div class="flex flex-col font-semibold text-sm text-[#171717]">
                            <span class="py-1">{{ $t('catalog.inactiveInBranch') }}</span>
                        </div>
                    </div>
                    <div class="flex gap-1 items-center" v-else-if="productInventoryLevelStatus === inventoryLevelStatuses.InStock">
                        <i class="ri-checkbox-circle-fill text-[#248726] text-2xl"></i>
                        <span class="font-semibold text-sm text-[#171717]">{{ $t('catalog.inStock') }}</span>
                    </div>
                    <div class="flex gap-1 items-start"
                         v-else-if="productInventoryLevelStatus === inventoryLevelStatuses.LimitedStock">
                        <i class="ri-alert-fill text-[#F4AB19] text-2xl"></i>
                        <div class="flex flex-col font-semibold text-sm text-[#171717]">
                            <span class="py-1">{{ $t('catalog.limitedStock') }}</span>
                            <span class="font-normal text-sm text-[#6E6E6E]">{{ $t('catalog.branchAvailability') }}</span>
                        </div>
                    </div>
                    <div class="font-normal text-sm text-[#6E6E6E]" v-else>{{ $t('catalog.branchAvailability') }}</div>
                </div>
                <template v-if="isPriceViewer">
                    <div v-if="loadingPrice" class="h-6">Loading Price</div>
                    <template v-else-if="hasPricingInformation">
                        <div class="flex" v-if="priceData.Price > 0">
                            <span class="font-normal text-base">{{ $t('globalCatalog.unitPrice') }}:</span>
                            <span class="font-semibold text-base text-[#000] pl-2">${{ formatPrice(priceData.Price) }}/{{ priceData.UnitOfMeasure }}</span>
                        </div>
                        <div class="" v-else>{{ $t('catalog.productPricing') }}</div>
                    </template>
                </template>

                <!-- Color and size selections -->
                <!-- Colors -->
                <div class="hidden md:flex md:flex-col gap-3" v-if="hasAvailableColors && !isVariantNotAvailableAtBranch">
                    <div class="md:flex md:flex-row">
                        <!--//class="flex items-center justify-between" -->
                        <span class="font-normal text-base text-[#6E6E6E] mr-1">{{ $t('catalog.color') }}: </span>
                        <span class="font-bold text-base text-[#171717]">{{ displayColor }}</span>
                        <IconButton :icon="colorsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                                ariaLabel="Toggle colors"
                                size="sm"
                                class="!border !rounded px-1 gap-1 !w-auto !h-auto"
                                @click="colorsOpen = !colorsOpen" />
                    </div>
                    <div class="flex gap-2 flex-wrap" v-show="colorsOpen">
                        <div id="global-catalog-variant-thumb-mobile" class="flex items-center justify-center w-14 h-14 rounded relative"
                             :class="variant.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer', (selectedColor === variant.colorName || selectedColor === variant.ColorName) ? 'outline outline-[3px] outline-[#762123]' : 'border !border-[#B9B9B9]'"
                             v-for="variant in availableColors"
                             :key="variant.itemCode || variant.ItemCode"
                             @click="setSelectedColor(variant)">
                            <img :src="getVariantImageUrl(variant)" width="42px" height="42px" />
                            <img :src="fortifiedIconImage" v-if="hasFortifiedCertification" class="absolute top-0 left-0 h-[20px]" width="20px" />
                            <template v-if="isColorInCart(variant)">
                                <div class="absolute left-1.5 top-1.5 right-1.5 bottom-1.5 bg-[#24872650]"></div>
                                <i class="ri-check-line absolute text-[#FFF] text-2xl"></i>
                            </template>
                            <div v-if="variant.disabled"
                                 class="absolute w-full h-full bg-gray-500 opacity-60"
                                 style="background-image:linear-gradient(45deg,transparent 48%,#fff 50%,transparent 52%);"></div>
                        </div>
                    </div>
                </div>

                <!-- Sizes -->
                <div class="hidden md:flex md:flex-col gap-3 mt-3" v-if="showSizePicker && !isVariantNotAvailableAtBranch">
                    <div class="md:flex md:flex-row">
                        <span class="font-normal text-base text-[#6E6E6E] mr-1">{{ $t('catalog.size') }}: </span>
                        <span class="font-bold text-base text-[#171717]">{{ selectedSize }}</span>
                        <IconButton :icon="sizesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                                ariaLabel="Toggle sizes"
                                size="sm"
                                class="!border !rounded px-1 gap-1 !w-auto !h-auto"
                                @click="sizesOpen = !sizesOpen" />
                    </div>
                    <div class="flex gap-2 flex-wrap" v-show="sizesOpen">
                        <div id="global-catalog-variant-thumb-mobile" class="flex items-center justify-center px-2 py-1 rounded relative"
                             :class="variant.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer', (selectedSize === variant.sizeName || selectedSize === variant.SizeName) ? 'outline outline-[3px] outline-[#762123]' : 'border !border-[#B9B9B9]'"
                             v-for="variant in availableSizes"
                             :key="variant.itemCode || variant.ItemCode"
                             @click="setSelectedSize(variant)">
                            <div>{{ variant.sizeName || variant.SizeName }}</div>
                            <template v-if="isSizeInCart(variant)">
                                <div class="absolute left-1.5 top-1.5 right-1.5 bottom-1.5 bg-[#24872650]"></div>
                                <i class="ri-check-line absolute text-[#FFF] text-2xl"></i>
                            </template>
                            <div v-if="variant.disabled"
                                 class="absolute w-full h-full bg-gray-500 opacity-60"
                                 style="background-image:linear-gradient(45deg,transparent 48%,#fff 50%,transparent 52%);"></div>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end items-center gap-px mt-4" :class="isOrderingViewer ? 'w-full md:w-48' : ''">
                    <template v-if="isOrderingViewer && isProductCatalog">
                        <BaseButton v-if="isItemAddedToCart"
                            variant="secondary"
                            size="custom"
                                id="global-catalog-delete-from-cart-mobile"
                            class="w-full flex items-center justify-center !rounded-l-md !rounded-r-none !bg-white !text-[#762123] h-10 px-3 !border-solid !border-2 !border-[#762123] hover:!bg-white active:!bg-white"
                                @click="deleteItemFromCart">
                            <span class="font-medium text-base whitespace-nowrap">
                                <i class="ri-delete-bin-6-line text-lg"></i>
                                {{ $t('catalog.added') }}
                            </span>
                        </BaseButton>
                        <BaseButton v-else id="global-catalog-add-to-cart-mobile"
                            size="custom"
                            class="w-full flex items-center justify-center !rounded-l-md !rounded-r-none h-10 px-3 !border-0"
                                @click="addItemToCart">
                            <span class="font-medium text-base whitespace-nowrap">{{ $t('catalog.addToCart') }}</span>
                        </BaseButton>
                        <div :class="`add-to-template-dropdown-${index} relative`">
                            <button id="global-catalog-add-to-template-toggle"
                                    class="flex items-center justify-center rounded-r-md bg-[#762123] text-[#FFF] h-10 px-2 focus:outline-none"
                                    @click="showAddToTemplateDropdown = !showAddToTemplateDropdown">
                                <i class="text-lg" :class="showAddToTemplateDropdown ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                            </button>
                            <div class="absolute bg-[#FFF] border border-[#D1D1D1] font-normal text-base right-0 text-center mt-1 rounded px-3 py-2 whitespace-nowrap
                            " v-if="showAddToTemplateDropdown">
                                <BaseButton size="custom" variant="secondaryFlat" id="global-catalog-add-to-template-menu" class="flex items-center justify-center h-full w-full" @click="openAddToTemplateModal()">
                                    {{ $t('catalog.addToTemplate') }}
                                </BaseButton>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="!isProductCatalog">
                        <!-- Add button for order catalog -->
                        <BaseButton size="custom" v-if="isItemAddedToBOM && isItemTemplateSingletonAddedToBOM"
                                id="global-catalog-delete-from-cart-mobile"
                                variant="secondary"
                                class="w-full flex items-center justify-center !rounded-md !text-[#762123] h-10 px-3 !border-solid !border-2 !border-[#762123]"
                                @click="deleteItemFromBOM">
                            <span class="font-medium text-base whitespace-nowrap">
                                <i class="ri-delete-bin-6-line text-lg"></i>
                                {{ $t('catalog.added') }}
                            </span>
                        </BaseButton>
                        <BaseButton size="custom" v-else id="global-catalog-add-to-cart-mobile"
                                class="w-full flex items-center justify-center !rounded-md h-10 px-3"
                                @click="addItemToBOM">
                            <span class="font-medium text-base whitespace-nowrap">{{ $t('catalog.add') }}</span>
                        </BaseButton>
                    </template>
                </div>

                <hr />
                <div class="font-semibold text-lg text-[#464646]">Product Description</div>
                <div class="font-normal text-base text-[#464646]">{{ product.webDescription }}</div>
                <div class="flex items-start gap-2 bg-[#F8F8F8] border !border-[#D1D1D1] rounded-md font-normal text-sm text-[#2E2E2E] p-3" v-if="hasFortifiedCertification">
                    <i class="ri-verified-badge-line text-[#2E2E2E] text-2xl"></i>
                    <div class="pt-1 text-[#2E2E2E] [&>p>a]:text-[#2E2E2E] [&>p>a]:underline [&>p>a]:underline-offset-2" v-html="fortifiedInformation"></div>
                </div>
                <hr />
                <div class="font-semibold text-lg text-[#464646]">Features</div>
                <div class="font-normal text-base text-[#464646]">
                    <ul class="pl-8">
                        <li v-for="feature in product.featureBullets" class="list-disc">
                            {{ feature }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>

    <!-- ProductCard -->
    <div id="global-catalog-product-card" class="border !border-[#D1D1D1] rounded-md p-4" v-else>
        <div class="flex flex-col">
            <div class="flex flex-col md:flex-row justify-between w-full">
                <div class="flex gap-4 mb-3">
                    <!-- Left Image -->
                    <div class=" w-[130px] h-[130px] relative">
                        <img id="global-catalog-product-image-mobile" :src="productImageUrl" class="w-full h-full rounded-md" @error="getMediaUrl('error')" />
                        <img :src="fortifiedIconImage" v-if="hasFortifiedCertification" class="absolute top-0 left-0 h-[42px]" width="42px" />
                    </div>
                    <!-- Content -->
                    <div class="flex flex-col items-start justify-start gap-1">
                        <div class="font-normal text-sm text-[#6E6E6E]">{{product.manufacturer}}</div>
                        <a id="global-catalog-product-title-mobile" class="font-semibold text-base !text-[#000] cursor-pointer" @click="setSelectedProduct()">{{product.webName}}</a>
                        <div class="font-normal text-sm text-[#000]">{{selectedVariant?.agilitySize}}</div>
                        <div class="font-normal text-sm text-[#464646]" v-if="isProductCatalog">
                            {{ $t('catalog.itemCode') }} {{ selectedVariant?.itemCode || selectedVariant?.ItemCode }}
                            <span v-if="selectedVariant?.manufacturerPartNumber || selectedVariant?.ManufacturerPartNumber">
                                | {{ $t('catalog.mfg') }} {{ selectedVariant?.manufacturerPartNumber || selectedVariant?.ManufacturerPartNumber }}
                            </span>
                        </div>
                        <div class="flex gap-1">
                            <i class="ri-folder-line text-[#464646]"></i>
                            <div class="font-normal text-sm text-[#6E6E6E]">{{ getFullCategoryName() }}</div>
                        </div>
                        <div class="flex gap-1">
                            <div class="">
                                <div v-if="isVariantNotAvailableAtBranch" class="flex gap-1 items-start">
                                    <i class="ri-error-warning-fill text-red-600 text-2xl flex-shrink-0"></i>
                                    <div class="flex flex-col font-semibold text-sm text-[#171717]">
                                        <span class="py-1">{{ $t('catalog.inactiveInBranch') }}</span>
                                    </div>
                                </div>
                                <div class="flex gap-1 items-center" v-else-if="productInventoryLevelStatus === inventoryLevelStatuses.InStock">
                                    <i class="ri-checkbox-circle-fill text-[#248726] text-2xl"></i>
                                    <span class="font-semibold text-sm text-[#171717]">{{ $t('catalog.inStock') }}</span>
                                </div>
                                <div class="flex gap-1 items-start"
                                     v-else-if="productInventoryLevelStatus === inventoryLevelStatuses.LimitedStock">
                                    <i class="ri-alert-fill text-[#F4AB19] text-2xl"></i>
                                    <div class="flex flex-col font-semibold text-sm text-[#171717]">
                                        <span class="py-1">{{ $t('catalog.limitedStock') }}</span>
                                        <span class="font-normal text-sm text-[#6E6E6E]">{{ $t('catalog.branchAvailability') }}</span>
                                    </div>
                                </div>
                                <div class="flex items-center font-normal text-sm text-[#6E6E6E]" v-else-if="!!branchCode">
                                    <i class="fas fa-question-circle text-2xl pr-2"></i>
                                    <span>{{ $t('catalog.branchAvailability') }}</span>
                                </div>
                            </div>
                        </div>
                        <!-- For Mobile Device -->
                        <div class="flex flex-col gap-2 md:hidden">
                            <div class="rounded-full px-2 py-1 font-semibold text-xs border border-[#FAD58C] bg-[#FDF9C8] text-[#69470C]" v-if="product.recommendation">Roof Hub Recommends</div>
                            <div v-if="branchEligiblePromotions.length" class="promoFlagContainers group relative rounded-full px-2 py-1 font-semibold text-xs border border-[#94E296] bg-[#E8FFCD] text-[#005101] inline-flex gap-1">
                                <span>Promo Eligible</span>
                                <span>({{ branchEligiblePromotions.length }})</span>
                                <span class="underline cursor-pointer" @click="toggleTooltip(index)">Details</span>

                                <ul class="promoDropDown absolute right-[-36%] bottom-[147%]
                                    hidden opacity-0
                                    group-hover:block group-hover:opacity-100
                                    bg-white border border-[#252323] rounded
                                    shadow-[2px_2px_4px_1px_rgba(0,0,0,0.2)]
                                    min-w-[321px] max-w-[350px] z-[1050]
                                    transition-all duration-200 ease-in-out
                                    list-none m-0 p-0
                                    before:content-['']
                                    before:block
                                    before:absolute
                                    before:w-[22px]
                                    before:h-[22px]
                                    before:bg-[#FFF]
                                    before:border-none
                                    before:rotate-[45deg]
                                    before:bottom-[-11px]
                                    before:right-[77px]
                                    before:shadow-[1px_1px_rgba(0,0,0,0.2)]
                                    overflow-visible
                                "
                                    :class="{ show: openIndex === index }"
                                    style="--tw-content: '';">
                                    <div class="max-h-[300px] overflow-y-auto">
                                    <li v-for="promo in branchEligiblePromotions"
                                        :key="promo.promotionName"
                                        class="p-3 border-b border-gray-300 last:border-none">
                                        <p>
                                            <span class="text-gray-700 text-lg font-semibold" v-if="promo.promotionName">
                                                {{ promo.promotionName }}
                                            </span>
                                            <br v-if="promo.promotionName" />
                                            <small class="text-gray-700 text-base" v-if="promo.detailedDescription">
                                                {{ promo.detailedDescription }}
                                            </small>
                                            <br v-if="promo.detailedDescription" />
                                            <small class="text-gray-700 text-base mt-4">
                                                Offer valid {{ formatDateForInput(promo.startDate) }} –
                                                {{ formatDateForInput(promo.endDate) }}
                                            </small>
                                        </p>
                                    </li>
                            </div>
                            </ul>
                        </div>

                    </div>
                </div>
            </div>
            <!-- Right content -->
            <div class="flex flex-col md:items-end md:justify-start gap-4">
                <template v-if="isPriceViewer">
                    <div v-if="loadingPrice" class="h-6">Loading Price</div>
                    <template v-else-if="hasPricingInformation">
                        <div class="flex justify-end" v-if="priceData.Price > 0">
                            <span class="font-normal text-base">{{ $t('globalCatalog.unitPrice') }}:</span>
                            <span class="font-semibold text-base text-[#000] pl-2">${{ formatPrice(priceData.Price) }}/{{ priceData.UnitOfMeasure }}</span>
                        </div>
                        <div class="" v-else>{{ $t('catalog.productPricing') }}</div>
                    </template>
                </template>
                <!-- For Desktop -->
                <div class="hidden md:flex flex-wrap justify-end gap-2">
                    <div class="rounded-full px-2 py-1 font-semibold text-xs border border-[#FAD58C] bg-[#FDF9C8] text-[#69470C]" v-if="product.recommendation">Roof Hub Recommends</div>
                    <div v-if="branchEligiblePromotions.length" class="promoFlagContainers group relative rounded-full px-2 py-1 font-semibold text-xs border border-[#94E296] bg-[#E8FFCD] text-[#005101] inline-flex gap-1">
                        <span>Promo Eligible</span>
                        <span>({{ branchEligiblePromotions.length }})</span>
                        <span class="underline cursor-pointer" @click="toggleTooltip(index)">Details</span>

                        <ul class="promoDropDown absolute right-[-14%] bottom-[147%]
                            hidden opacity-0
                            group-hover:block group-hover:opacity-100
                            bg-white border border-[#252323] rounded
                            shadow-[2px_2px_4px_1px_rgba(0,0,0,0.2)]
                            min-w-[321px] max-w-[350px] z-[1050]
                            transition-all duration-200 ease-in-out
                            list-none m-0 p-0
                            before:content-['']
                            before:block
                            before:absolute
                            before:w-[22px]
                            before:h-[22px]
                            before:bg-[#FFF]
                            before:border-none
                            before:rotate-[45deg]
                            before:bottom-[-11px]
                            before:right-[45px]
                            before:shadow-[1px_1px_rgba(0,0,0,0.2)]
                            overflow-visible
                        "
                            :class="{ show: openIndex === index }"
                            style="--tw-content: '';">
                            <div class="max-h-[300px] overflow-y-auto">
                            <li v-for="promo in branchEligiblePromotions"
                                :key="promo.promotionName"
                                class="p-3 border-b border-gray-300 last:border-none">
                                <p>
                                    <span class="text-gray-700 text-lg font-semibold" v-if="promo.promotionName">
                                        {{ promo.promotionName }}
                                    </span>
                                    <br v-if="promo.promotionName" />
                                    <small class="text-gray-700 text-base" v-if="promo.detailedDescription">
                                        {{ promo.detailedDescription }}
                                    </small>
                                    <br v-if="promo.detailedDescription" />
                                    <small class="text-gray-700 text-base mt-4">
                                        Offer valid {{ formatDateForInput(promo.startDate) }} –
                                        {{ formatDateForInput(promo.endDate) }}
                                    </small>
                                </p>
                            </li>
                    </div>
                    </ul>
                </div>
            </div>
            <div class="flex flex-col md:flex-row gap-3 justify-end">
                <div class="flex justify-end items-center gap-px md:w-40" :class="isOrderingViewer ? 'w-full' : ''">
                    <template v-if="isOrderingViewer && isProductCatalog">
                        <BaseButton v-if="isItemAddedToCart"
                            variant="secondary"
                            size="custom"
                                id="global-catalog-delete-from-cart-mobile"
                            class="w-full md:w-32 flex items-center justify-center !rounded-l-md !rounded-r-none !bg-white !text-[#762123] h-10 px-3 !border-solid !border-2 !border-[#762123] hover:!bg-white active:!bg-white"
                                @click="deleteItemFromCart">
                            <span class="font-medium text-base whitespace-nowrap">
                                <i class="ri-delete-bin-6-line text-lg"></i>
                                {{ $t('catalog.added') }}
                            </span>
                        </BaseButton>
                        <BaseButton v-else id="global-catalog-add-to-cart-mobile"
                            size="custom"
                            class="w-full md:w-32 flex items-center justify-center !rounded-l-md !rounded-r-none h-10 px-3 !border-0"
                                @click="addItemToCart">
                            <span class="font-medium text-base whitespace-nowrap">{{ $t('catalog.addToCart') }}</span>
                        </BaseButton>
                        <div :class="`add-to-template-dropdown-${index} relative`">
                            <button id="global-catalog-add-to-template-toggle"
                                    class="flex items-center justify-center rounded-r-md bg-[#762123] text-[#FFF] h-10 px-2 focus:outline-none"
                                    @click="showAddToTemplateDropdown = !showAddToTemplateDropdown">
                                <i class="text-lg" :class="showAddToTemplateDropdown ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                            </button>
                            <div class="absolute bg-[#FFF] border border-[#D1D1D1] font-normal text-base right-0 text-center mt-1 rounded px-3 py-2 whitespace-nowrap
                                    " v-if="showAddToTemplateDropdown">
                                <BaseButton size="custom" variant="secondaryFlat" id="global-catalog-add-to-template-menu" class="flex items-center justify-center h-full w-full" @click="openAddToTemplateModal()">
                                    {{ $t('catalog.addToTemplate') }}
                                </BaseButton>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="!isProductCatalog">
                        <!-- Add button for order catalog -->
                        <BaseButton size="custom" v-if="isItemAddedToBOM"
                                id="global-catalog-delete-from-cart-mobile"
                                variant="secondary"
                                class="w-full md:w-40 flex items-center justify-center !rounded-md !text-[#762123] h-10 px-3 !border-solid !border-2 !border-[#762123]"
                                @click="deleteItemFromBOM">
                            <span class="font-medium text-base whitespace-nowrap">
                                <i class="ri-delete-bin-6-line text-lg"></i>
                                {{ $t('catalog.added') }}
                            </span>
                        </BaseButton>

                        <BaseButton size="custom" v-else id="global-catalog-add-to-cart-mobile"
                                class="w-full md:w-40 flex items-center justify-center !rounded-md h-10 px-3"
                                @click="addItemToBOM">
                            <span class="font-medium text-base whitespace-nowrap">{{ $t('catalog.add') }}</span>
                        </BaseButton>
                    </template>
                </div>
            </div>
        </div>
    </div>
    <!-- Product images -->
    <!-- Color and size selections -->
    <div class="hidden md:flex md:flex-col gap-3" v-if="hasAvailableColors && !isVariantNotAvailableAtBranch">
        <div class="md:flex md:flex-row">
            <!--//class="flex items-center justify-between" -->
            <span class="font-normal text-base text-[#6E6E6E] mr-1">{{ $t('catalog.color') }}: </span>
            <span class="font-bold text-base text-[#171717]">{{ displayColor }}</span>
            <IconButton :icon="colorsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                    ariaLabel="Toggle colors"
                    size="sm"
                    class="!border !rounded px-1 gap-1 !w-auto !h-auto"
                    @click="colorsOpen = !colorsOpen" />
        </div>
        <div class="flex gap-2 flex-wrap" v-show="colorsOpen">
            <div id="global-catalog-variant-thumb-mobile" class="flex items-center justify-center w-14 h-14 rounded relative"
                 :class="variant.disabled ? 'cursor-not-allowed' : 'cursor-pointer', selectedColor === variant.colorName ? 'outline outline-[3px] outline-[#762123]' : 'border !border-[#B9B9B9]'"
                 v-for="variant in availableColors"
                 @click="setSelectedColor(variant)">
                <img :src="getVariantImageUrl(variant)" width="42px" height="42px" />
                <img :src="fortifiedIconImage" v-if="hasFortifiedCertification" class="absolute top-0 left-0 h-[20px]" width="20px" />
                <template v-if="isColorInCart(variant)">
                    <div class="absolute left-1.5 top-1.5 right-1.5 bottom-1.5 bg-[#24872650]"></div>
                    <i class="ri-check-line absolute text-[#FFF] text-2xl"></i>
                </template>
                <div v-if="variant.disabled"
                     class="absolute w-full h-full bg-gray-500 opacity-60"
                     style="background-image:linear-gradient(45deg,transparent 48%,#fff 50%,transparent 52%);"></div>
            </div>
        </div>
    </div>

    <!-- Read-only Color/Size that matched the catalog filter when the product does not offer it as a choice -->
    <div class="flex flex-col gap-1 mt-3" v-if="filterMatchLabels.length">
        <div v-for="label in filterMatchLabels" :key="label.key">
            <span class="font-normal text-base text-[#6E6E6E] mr-1">{{ $t(label.key) }}: </span>
            <span class="font-bold text-base text-[#171717]">{{ label.value }}</span>
        </div>
    </div>

    <div class="hidden md:flex md:flex-col gap-3 mt-3" v-if="showSizePicker && !isVariantNotAvailableAtBranch">
        <div class="md:flex md:flex-row">
            <span class="font-normal text-base text-[#6E6E6E] mr-1">{{ $t('catalog.size') }}: </span>
            <span class="font-bold text-base text-[#171717]">{{ selectedSize }}</span>
            <IconButton :icon="sizesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                    ariaLabel="Toggle sizes"
                    size="sm"
                    class="!border !rounded px-1 gap-1 !w-auto !h-auto"
                    @click="sizesOpen = !sizesOpen" />
        </div>
        <div class="flex gap-2 flex-wrap" v-show="sizesOpen">
            <div id="global-catalog-variant-thumb-mobile" class="flex items-center justify-center px-2 py-1 rounded relative"
                 :class="variant.disabled ? 'cursor-not-allowed' : 'cursor-pointer', selectedSize === variant.sizeName ? 'outline outline-[3px] outline-[#762123]' : 'border !border-[#B9B9B9]'"
                 v-for="variant in availableSizes"
                 @click="setSelectedSize(variant)">
                <div>{{ variant.sizeName }}</div>
                <template v-if="isSizeInCart(variant)">
                    <div class="absolute left-1.5 top-1.5 right-1.5 bottom-1.5 bg-[#24872650]"></div>
                    <i class="ri-check-line absolute text-[#FFF] text-2xl"></i>
                </template>
                <div v-if="variant.disabled"
                     class="absolute w-full h-full bg-gray-500 opacity-60"
                     style="background-image:linear-gradient(45deg,transparent 48%,#fff 50%,transparent 52%);"></div>
            </div>
        </div>
    </div>
    </div>
    </div>

    <AlertToast v-if="showError" :message="$t('catalog.addToTemplateError')" type="error" @close="showError = false" />
    <Modal :showModal="showModal" @close="showModal = false">
        <template #header>
            <div class="flex items-center space-x-2">
                {{ $t('catalog.addToTemplate') }}
                <img v-if="loadingAddToTemplate" class="h-10 w-10 ml-3" :src="`${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_BASE_PATH}Loader.gif`" />
            </div>
        </template>
        <template #content>
            <div class="flex flex-col">
                <div class="font-semibold text-base text-[#464646]">Item selected:</div>
                <div class="flex gap-4 my-3">
                    <!-- Left Image -->
                    <div class=" w-[100px] h-[100px] relative">
                        <img :src="productImageUrl" class="w-full h-full rounded-md" @error="getMediaUrl('error')" />
                        <img :src="fortifiedIconImage" v-if="hasFortifiedCertification" class="absolute top-0 left-0 h-[42px]" width="42px" />
                    </div>
                    <!-- Content -->
                    <div class="flex flex-col items-start justify-start gap-1">
                        <div class="font-normal text-sm text-[#6E6E6E]">{{product.manufacturer}}</div>
                        <a class="font-semibold text-base !text-[#171717]">{{product.webName}}</a>
                        <div class="font-normal text-sm text-[#464646]">{{selectedVariant?.agilitySize}}</div>
                    </div>
                </div>
                <div class="flex flex-col bg-[#F8F8F8] p-3 rounded relative">
                    <div class="flex items-center justify-between gap-8">
                        <div ref="flexContainer" class="flex w-[90%] md:max-w-[48rem] p-px">
                            <div v-for="brand in brandsToShow" :key="brand" @click="selectedBrand = brand"
                                 class="cursor-pointer whitespace-nowrap py-1 px-3 -ml-px border !border-[#D1D1D1] first:rounded-l-md last:rounded-r-md font-normal text-base"
                                 :class="selectedBrand === brand ? 'bg-[#F8F8F8] text-[#762123] border-b-[#762123] shadow-[0px_3px_12px_0px_#00000059_inset,0px_4px_4px_0px_#00000040_inset]' : 'bg-[#FFF] text-[#545454]'">
                                {{ brand }}
                            </div>
                        </div>
                        <div class="show-more-brands-dropdown relative dropdown" v-if="showMoreBrands">
                            <IconButton icon="ri-more-fill" ariaLabel="Show more brands" size="lg" class="!bg-[#F8F8F8] !rounded border" @click="showAllBrands = !showAllBrands" />
                            <div class="dropdown-menu dropdown-menu-right mt-1 p-3 w-52 max-h-60 overflow-y-auto" role="menu" v-if="showAllBrands">
                                <ul class="list-disc px-3">
                                    <li class="bg-[#FFF] cursor-pointer" v-for="brand in brands">
                                        <BaseButton size="custom" variant="secondaryFlat" class="w-full text-left"
                                                @click="setSelectedBrandFromDropdown(brand)"
                                                :class="brand === selectedBrand ? '!text-[#762123] !font-bold' : ''">
                                            {{ brand }}
                                        </BaseButton>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center gap-2 py-3">
                        <input type="checkbox" class="custom-checkbox border-1.5 !border-[#2E2E2E] w-[20px] !min-h-[20px]" v-model="isCheckAll" @change="checkAllTemplates" />
                        <label class="font-normal text-base m-0">{{ t('templates.checkAll') }}</label>
                    </div>
                    <div class="pr-2">
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div v-for="template in filteredTemplates" class="flex flex-col border !border-[#D1D1D1] rounded-lg p-3 bg-[#fff] text-[#171717] max-w-48">
                                <div class="flex-1 flex flex-col gap-2">
                                    <div class="">
                                        <input type="checkbox" class="custom-checkbox border-1.5 !border-[#2E2E2E] w-[20px] !min-h-[20px]" :value="template" v-model="selectedTemplates" />
                                    </div>
                                    <img :src="template.image" alt="logo" />
                                    <div class="flex justify-center text-center font-bold text-base text-[#000]">{{ template.title }}</div>
                                    <div class="flex justify-center text-center font-normal text-sm leading-[130%] tracking-[0%] text-[#000]">{{ template.description }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-between items-center gap-2 mt-3 border-t pt-3">
                <BaseButton size="custom" id="global-catalog-add-to-template-modal-cancel"
                        variant="secondaryFlat"
                        class="rounded py-2 px-4 font-semibold text-base"
                        @click="showModal = false">
                    Cancel
                </BaseButton>
                <BaseButton size="custom" id="global-catalog-add-to-template-modal-add"
                        class="rounded py-2 px-4 font-semibold text-base"
                        :disabled="selectedTemplates.length === 0 || loadingAddToTemplate"
                        @click="addToTemplate()">
                    Add
                </BaseButton>
            </div>
        </template>
    </Modal>

</template>

<script setup>
import { 
    MEDIA_ASSET_BASE_URL, 
    MEDIA_ASSET_BASE_PATH,
    MEDIA_ASSET_PRODUCT_IMG,
    MEDIA_ASSET_ORDERS_PATH
} from '@/config/constants';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import moment from 'moment';
import { useGlobalCatalogStore } from "@/stores/globalCatalogStore";
import { useOrdersPageStore } from "@/stores/orderTemplateStore";
import { useCreateEstimateStore } from "@/stores/createEstimate";
import { useI18n } from 'vue-i18n'
import { getLanguage } from '@/utilities/cookies';
import AlertToast from '@/components/atoms/AlertToast.vue';
import Modal from '@/components/common/Modal.vue';
import BaseButton from '@/components/atoms/buttons/BaseButton.vue';
import IconButton from '@/components/atoms/buttons/IconButton.vue';
import orderTemplateService from '@/services/orderTemplateService';
import OrderUtils from '@/utils/orderUtils.js';

/** MDS cart COLOR may be legacy "Size Color". Flag-OFF callers must not use this. */
function extractPureColorForMdsCart(combinedColor, size) {
    const color = String(combinedColor || '').trim()
    const sizeVal = String(size || '').trim()
    if (!color || !sizeVal) return color
    const colorLower = color.toLowerCase()
    const sizeLower = sizeVal.toLowerCase()
    if (colorLower.startsWith(`${sizeLower} `)) {
        return color.slice(sizeVal.length).trim()
    }
    if (colorLower.endsWith(` ${sizeLower}`)) {
        return color.slice(0, -(sizeVal.length + 1)).trim()
    }
    return color
}

function mdsCartColorEquals(item, colorName) {
    if (!colorName) return false
    const raw = item?.SelectedColor || item?.selectedColor || item?.Options?.color || ''
    if (!raw) return false
    const want = String(colorName).trim().toLowerCase()
    if (String(raw).trim().toLowerCase() === want) return true
    const size = item?.selectedSize || item?.SelectedSize || item?.Options?.size || ''
    const stripped = extractPureColorForMdsCart(raw, size)
    if (stripped.toLowerCase() === want) return true
    return String(raw).trim().toLowerCase().endsWith(` ${want}`)
}

const { t, locale } = useI18n()
locale.value = getLanguage();

const fortifiedInformation = ref(window.fortifiedInformation);
const showError = ref(false);
const dropdownOpen = ref(false);
const dropdownRef = ref(null);

const globalCatalogStore = useGlobalCatalogStore();
const createEstimateStore = useCreateEstimateStore();

const componentToShow = computed(() => globalCatalogStore.componentToShow);
const branchCode = computed(() =>   globalCatalogStore.selectedBranch?.BranchCode ?? '');
const isItemTemplateSingletonAddedToBOM = ref(true);
const selectedShipToId = computed(() => globalCatalogStore.selectedJobAccount?.ShipToSequenceId ?? null);

const props = defineProps({
    product: {
        type: Object,
        default: {}
    },
    index: Number
})
const getMediaUrl = (assetUri) => {
    const defaultImage = `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_ORDERS_PATH}noProduct.svg`
    if(assetUri === 'error'){
        productImageUrl.value = defaultImage
        return;
    }
    if ( assetUri ) {
        return `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_PRODUCT_IMG}${assetUri}.jpg`;
    }
    return defaultImage
}

    // Helper to get variant image URL with proper fallback logic
    const getVariantImageUrl = (variant) => {
        if (!variant) return getMediaUrl(null);

        // Pull the mediaAssets array (handles camelCase and PascalCase)
        const mediaAssets = variant.mediaAssets || variant.MediaAssets || [];

        const getType = (a) => a?.assetType || a?.AssetType;
        const getUri = (a) => a?.assetUri || a?.AssetUri;

        // Filter to item-level images only (skip ProductDataSheet, ProductPrimaryImage, etc.)
        const itemAssetTypes = ['ItemPrimaryImage', 'ItemAlternateImage'];
        const itemAssets = mediaAssets.filter(a => itemAssetTypes.includes(getType(a)));

        // Backend sometimes returns multiple ItemPrimaryImage entries for the same variant,
        // where one is mistakenly attached (e.g. it points to a different itemCode).
        // The correct one usually has the variant's colorName embedded in the assetUri.
        const colorName = variant.colorName || variant.ColorName || '';
        const colorToken = colorName.toLowerCase().trim();

        let chosen = null;

        if (colorToken) {
            // First preference: ItemPrimaryImage whose URI contains the colorName
            chosen = itemAssets.find(a =>
                getType(a) === 'ItemPrimaryImage' &&
                getUri(a)?.toLowerCase().includes(colorToken)
            );

            // Second preference: any item-level asset whose URI contains the colorName
            if (!chosen) {
                chosen = itemAssets.find(a => getUri(a)?.toLowerCase().includes(colorToken));
            }
        }

        // Third preference: first ItemPrimaryImage
        if (!chosen) {
            chosen = itemAssets.find(a => getType(a) === 'ItemPrimaryImage');
        }

        // Fourth preference: first item-level asset of any kind
        if (!chosen) chosen = itemAssets[0];

        let assetUri = getUri(chosen);

        // Fallback to imageUrl property
        if (!assetUri) {
            assetUri = variant.imageUrl || variant.ImageUrl;
        }

        return getMediaUrl(assetUri);
    };

const fortifiedIconImage = `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_BASE_PATH}fortified-logo-roof.svg`;
const productImageUrl = ref('');
const selectedVariant = ref(null);
const uom = computed(() => {
    return globalCatalogStore.selectedProductUom || selectedVariant.value?.uoms.find(uom => uom.isDefaultMeasure === true)?.name;
});
const productsPricing = computed(() => globalCatalogStore.productsPricing);
const priceData = computed(() => productsPricing.value.find(pp => pp.ItemCode === selectedVariant?.value?.itemCode));
const loadingPrice = ref(false);
const getPricing = async () => {
    if(branchCode.value) {
        try {
            loadingPrice.value = true;
            await globalCatalogStore.getProductPricing(selectedVariant?.value?.itemCode, uom.value);
        } catch (error) {
            console.error('Error fetch pricing details')
        } finally {
            loadingPrice.value = false;
        }
    }
}
const loadingInventoryLevel = ref(false)
const getProductInventoryLevel = async () => {
    try {
        loadingInventoryLevel.value = true;
        const shippingMethod = globalCatalogStore.bomItemDeliveryType || null;
        await globalCatalogStore.getProductInventoryLevel(selectedVariant?.value?.itemCode, null, shippingMethod, true)
    } catch (error) {
        console.error('Error fetching product inventory level')
    } finally {
        loadingInventoryLevel.value = false;
    }
}

const showAddToTemplateDropdown = ref(false);
const hasUserSelectedVariant = computed(() => globalCatalogStore.hasUserSelectedVariant);
const isTemplateSingleton = computed(() => globalCatalogStore.isTemplateSingleton);

// Variants handling
const selectedColor = ref(null);
const selectedSize = ref(null);

// Check if product has COLOR as a selectable feature.
const hasColorFeature = computed(() => {
    if (!globalCatalogStore.isOptionSpecific) return false;
    const features = props.product.selectableFeatures || [];
    return features.includes('COLOR');
});

// Check if product has SIZE as a selectable feature.
const hasSizeFeature = computed(() => {
    if (!globalCatalogStore.isOptionSpecific) return false;
    const features = props.product.selectableFeatures || [];
    return features.includes('SIZE');
});

const isSingleton = computed(() => {
    const features = props.product.selectableFeatures || [];
    return features.length === 0 || features.includes('N/A');
});

const availableColors = computed(() => {
    if (!hasColorFeature.value) return [];

    const seen = new Set();
    const colors = [];

    for (const variant of (props.product.variants || [])) {
        const colorName = variant.colorName || variant.ColorName;
        if (!colorName || seen.has(colorName)) continue;

        seen.add(colorName);

        // Color is available if ANY variant of this color is available at branch
        const colorAvailable = (props.product.variants || []).some(v =>
            (v.colorName || v.ColorName) === colorName &&
            (v.availableAtBranch !== false && v.AvailableAtBranch !== false)
        );

        colors.push({
            ...variant,
            colorName: colorName,
            disabled: !colorAvailable
        });
    }

    return colors;
});

const displayColor = computed(() => {
    if (!selectedColor.value) return '';      
    // Clean up display - remove size if concatenated
    if (selectedSize.value && selectedColor.value.includes(selectedSize.value)) {
        return selectedColor.value.replace(selectedSize.value, '').trim();
    }
    return selectedColor.value;
});

const availableSizes = computed(() => {
    if (!hasSizeFeature.value) return [];
    // COLOR+SIZE: do not load sizes until color is selected (SIZE-only still loads immediately)
    if (hasColorFeature.value && !selectedColor.value) return [];

    const seen = new Set();
    const sizes = [];

    for (const variant of (props.product.variants || [])) {
        const sizeName = variant.sizeName || variant.SizeName;
        if (!sizeName || seen.has(sizeName)) continue;

        // Color selected (or SIZE-only): only sizes that exist for that color / all sizes
        if (selectedColor.value) {
            const matchingVariant = props.product.variants.find(v => 
                (v.colorName || v.ColorName) === selectedColor.value && 
                (v.sizeName || v.SizeName) === sizeName
            );

            if (!matchingVariant) continue; // Skip sizes not available for selected color

            seen.add(sizeName);
            sizes.push({
                ...matchingVariant,
                sizeName: sizeName,
                disabled: false // Valid sizes are never disabled when COLOR is selected
            });
        } else {
            // SIZE-only product (no COLOR feature) — show all unique sizes
            seen.add(sizeName);
            sizes.push({
                ...variant,
                sizeName: sizeName,
                disabled: false
            });
        }
    }

    return sizes;
});

const hasAvailableColors = computed(() => availableColors.value?.some(c => !c.disabled));
const hasAvailableSizes = computed(() => availableSizes.value?.some(s => !s.disabled));

// A product can match a Color/Size catalog filter through item data even when it does not offer that
// option as a choice; show the matched value read-only so it is clear why the product is listed.
const filterMatchLabels = computed(() => {
    const variants = props.product.variants || [];
    const labels = [];
    const addLabel = (selected, hasFeature, getValue, key) => {
        if (!selected?.length || hasFeature) return;
        const value = variants.map(getValue).find(v => v && selected.includes(v));
        if (value) labels.push({ key, value });
    };
    addLabel(globalCatalogStore.selectedColors, hasColorFeature.value, v => v.colorName || v.ColorName, 'catalog.color');
    addLabel(globalCatalogStore.selectedSizes, hasSizeFeature.value, v => v.sizeName || v.SizeName, 'catalog.size');
    return labels;
});
// COLOR+SIZE before color: show size UI shell with no options loaded yet
const showSizePicker = computed(() => {
    if (!hasSizeFeature.value) return false;
    if (hasColorFeature.value && !selectedColor.value) return true;
    return hasAvailableSizes.value;
});

const setSelectedColor = variant => {
    if (variant.disabled) return; // Don't allow selecting disabled colors

    const newColor = variant.colorName || variant.ColorName;
    selectedColor.value = newColor;
    selectedVariant.value = null;
    globalCatalogStore.hasUserSelectedVariant = true;

    console.log('[ProductCard] Color selected:', newColor);

    const validSizes = availableSizes.value.filter(s => !s.disabled);

    if (validSizes.length === 1) {
        selectedSize.value = validSizes[0].sizeName || validSizes[0].SizeName;
        console.log('[ProductCard] Auto-selected only valid size:', selectedSize.value);
    } else if (selectedSize.value) {
        const previousSizeStillValid = validSizes.some(s => 
            (s.sizeName || s.SizeName) === selectedSize.value
        );

        if (!previousSizeStillValid && validSizes.length > 0) {
            // Previous SIZE invalid - auto-select first valid SIZE
            selectedSize.value = validSizes[0].sizeName || validSizes[0].SizeName;
            console.log('[ProductCard] Previous size invalid, auto-selected first valid:', selectedSize.value);
        } else if (!previousSizeStillValid) {
            selectedSize.value = null;
        }
    }

    // Find and set the matching variant
    if (hasSizeFeature.value && selectedSize.value) {
        const matchingVariant = props.product.variants.find(v => 
            (v.colorName || v.ColorName) === newColor && 
            (v.sizeName || v.SizeName) === selectedSize.value
        );
        if (matchingVariant) {
            setSelectedVariant(matchingVariant, true);
        }
    } else if (!hasSizeFeature.value) {
        // No SIZE feature - just COLOR
        const matchingVariant = props.product.variants.find(v => 
            (v.colorName || v.ColorName) === newColor
        );
        if (matchingVariant) {
            setSelectedVariant(matchingVariant, true);
        }
    }
}

const setSelectedSize = variant => {
    if (variant.disabled) return; // Don't allow selecting disabled sizes

    const newSize = variant.sizeName || variant.SizeName;
    selectedSize.value = newSize;
    selectedVariant.value = null;
    globalCatalogStore.hasUserSelectedVariant = true;

    console.log('[ProductCard] Size selected:', newSize);

    // Find matching variant with both COLOR and SIZE
    if (hasColorFeature.value && selectedColor.value) {
        const matchingVariant = props.product.variants.find(v => 
            (v.colorName || v.ColorName) === selectedColor.value && 
            (v.sizeName || v.SizeName) === newSize
        );
        if (matchingVariant) {
            setSelectedVariant(matchingVariant, true);
        }
    } else if (!hasColorFeature.value) {
        // No COLOR feature - just SIZE
        const matchingVariant = props.product.variants.find(v => 
            (v.sizeName || v.SizeName) === newSize
        );
        if (matchingVariant) {
            setSelectedVariant(matchingVariant, true);
        }
    }
}

// UI toggles
const colorsOpen = ref(true);
const sizesOpen = ref(true);

const inventoryLevelStatuses = ref({
    "InStock": 0,
    "LimitedStock": 1,
    "OutOfStock": 2,
    "Unknown": 3
})
const isVariantActiveAtBranch = (variant) => {
    if (!variant) return false;
    const flags = [
        variant.availableAtBranch,
        variant.AvailableAtBranch,
        variant.isAvailableAtBranch,
        variant.IsAvailableAtBranch
    ];
    if (flags.some(f => f === false)) return false;
    if (flags.some(f => f === true)) return true;
    return true;
};

const hasAnyActiveVariantAtBranch = computed(() => {
    const variants = props.product?.variants || [];
    if (!variants.length) return false;
    return variants.some(isVariantActiveAtBranch);
});

const isVariantNotAvailableAtBranch = computed(() => {
    const variants = props.product?.variants || [];
    if (!variants.length) return true;

    return !hasAnyActiveVariantAtBranch.value;
});

const productInventoryLevelStatus = computed(() => {
    // When the selected variant is not active at the branch, inventory level
    // status is misleading (it aggregates nearby branches / time-fence rules).
    // Surface "not active at branch" instead, matching the BOM list behaviour.
    if (isVariantNotAvailableAtBranch.value) {
        return null;
    }
    if (!selectedVariant.value?.itemCode) {
        return null;
    }
    const status = globalCatalogStore.productInventoryLevelStatus.find(s => s.ItemCode === selectedVariant.value.itemCode);
    return status ? status.InventoryLevelStatus : null;
});

const setSelectedVariant = (variant, shouldFetchPricing = false) => {
    selectedVariant.value = variant;
    if (shouldFetchPricing) {
        // If this is the active product in the PDP, keep the global selection in sync with manual user selections
        if (globalCatalogStore.selectedProduct?.productId === props.product.productId) {
            globalCatalogStore.selectedItemCode = variant.itemCode || variant.ItemCode;
        }
        getPricing();
    }
    if ( productInventoryLevelStatus.value === null) {
        getProductInventoryLevel();
    }
}

const open = ref({});
const toggleTooltip = (index) => {
    open.value[index] = !open.value[index]
}
const hasFortifiedCertification = computed(() => selectedVariant?.value?.certifications.includes("Fortified"));

watch(
    [() => selectedVariant.value, () => props.product],
    ([newVariant]) => {
        if (!newVariant) return;
        productImageUrl.value = getVariantImageUrl(newVariant);
    },
    { immediate: true }
);

// Reset selections immediately when branch changes
watch(
    branchCode,
    () => {
        selectedVariant.value = null;
        selectedColor.value = null;
        selectedSize.value = null;
    }
);

    watch(
        () => props.product.variants,
        (newVariants) => {
            if (!newVariants?.length) {
                selectedVariant.value = null;
                selectedColor.value = null;
                selectedSize.value = null;
                return;
            }

            // If singleton (N/A), don't show selectors
            if (isSingleton.value) {
                const firstVariant = newVariants[0];
                setSelectedVariant(firstVariant);
                selectedColor.value = null;
                selectedSize.value = null;
                return;
            }

            // Check if product has pre-selected color/size (e.g., from order/template grid)

            // Priority 1: Direct properties (selectedColor, selectedSize)
            const incomingColor = props.product?.selectedColor || props.product?.SelectedColor;
            const incomingSize = props.product?.selectedSize || props.product?.SelectedSize;

            // Priority 2: From selectedVariant object
            const incomingVariant = props.product?.selectedVariant;
            const variantColor = incomingVariant?.colorName || incomingVariant?.ColorName;
            const variantSize = incomingVariant?.sizeName || incomingVariant?.SizeName;

            // Use direct properties if available, otherwise use variant properties
            const finalColor = incomingColor || variantColor;
            const finalSize = incomingSize || variantSize;

            // Pre-populate selections only when explicitly provided by upstream data.
            // Do not default-select for quick-search add flow.
            selectedColor.value = finalColor && hasColorFeature.value ? finalColor : null;
            selectedSize.value = finalSize && hasSizeFeature.value ? finalSize : null;

            // Find and set the matching variant
            let chosen = null;

            if (hasColorFeature.value && hasSizeFeature.value) {
                    // Both COLOR and SIZE
                    chosen = newVariants.find(v => 
                        (v.colorName || v.ColorName) === selectedColor.value && 
                        (v.sizeName || v.SizeName) === selectedSize.value
                    );
            } else if (hasColorFeature.value) {
                    // Only COLOR
                    chosen = newVariants.find(v => 
                        (v.colorName || v.ColorName) === selectedColor.value
                    );
            } else if (hasSizeFeature.value) {
                    // Only SIZE
                    chosen = newVariants.find(v => 
                        (v.sizeName || v.SizeName) === selectedSize.value
                    );
                }

            if (chosen) {
                setSelectedVariant(chosen);
            } else {
                selectedVariant.value = null;
            }
        },
        { immediate: true }
    );

const getChildCategory = item => {
    if (!item) return null;
    const children = item.categories || item.Categories;
    if (Array.isArray(children) && children.length > 0) {
        return getChildCategory(children[0]);
    }
    return item;
}

const getFullCategoryName = () => {
    const categories = props.product?.categories || props.product?.Categories || [];
    if (!Array.isArray(categories) || categories.length === 0) {
        return props.product?.categoryDisplayName
            || props.product?.CategoryDisplayName
            || props.product?.category
            || props.product?.Category
            || '';
    }
    const item = getChildCategory(categories[0]);
    return item?.fullCategoryName || item?.FullCategoryName || item?.categoryName || item?.CategoryName || '';
}

const hasVariants = computed(() => {
    if (!globalCatalogStore.isOptionSpecific) return false;

    return props.product.variants.length > 0
});
const hasPricingInformation = computed(() => {
    if (!hasVariants.value) return false;
    return priceData.value && !priceData.value?.ErrorRetrievingPrice;
})

const branchEligiblePromotions = computed(() => {
    if (!branchCode.value) {
        return props.product.promotions;
    }
    const promotions = props.product.promotions;
    return promotions.filter(promotion => promotion.branchCode === branchCode.value || promotion.branchCode === null || promotion.branchCode === '');

});


const addItemToCart = () => {
    globalCatalogStore.addItemToCart(props.product, selectedVariant.value)
}
const deleteItemFromCart = () => {
    const selectedVariantIndex = globalCatalogStore.globalCartItems.findIndex(item => item.itemCode === selectedVariant?.value?.itemCode);
    if ( selectedVariantIndex >= 0 ) {
        globalCatalogStore.deleteItemFromCart(selectedVariantIndex)
    }
}

const isItemAddedToCart = computed(() => globalCatalogStore.globalCartItems.some(item => item.itemCode === selectedVariant?.value?.itemCode));
const globalCartItemCodes = computed(() => globalCatalogStore.globalCartItems.map(item => item.itemCode));

const itemCodesAddedToBOM = computed(() => {
    // Use centralized computed from store
    return globalCatalogStore.cartItemsForCurrentTier.map(product => product.itemCode || product.ItemCode);
});

const mdsCartCoversProduct = computed(() => {
    const pid = props.product?.productId ?? props.product?.ProductId
    if (pid == null) return false
    return globalCatalogStore.cartItemsForCurrentTier.some(item =>
        String(item.productId || item.ProductId) === String(pid)
    )
})

const isItemInCart = itemCode => {
    if (!itemCode) return false;
    if (!hasUserSelectedVariant.value && !isProductCatalog.value) return false;

    return [...globalCartItemCodes.value, ...itemCodesAddedToBOM.value].includes(itemCode)
};

/** MDS: color chip is "in cart" if any BOM/cart SKU of this product has that color */
const isColorInCart = (colorVariant) => {
    if (!hasUserSelectedVariant.value && !isProductCatalog.value && !mdsCartCoversProduct.value) return false;

    const colorName = colorVariant?.colorName || colorVariant?.ColorName;
    if (!colorName) return isItemInCart(colorVariant?.itemCode || colorVariant?.ItemCode);

    const productId = props.product?.productId || props.product?.ProductId;
    const colorItemCodes = new Set(
        (props.product?.variants || [])
            .filter(v => (v.colorName || v.ColorName) === colorName)
            .map(v => v.itemCode || v.ItemCode)
            .filter(Boolean)
    );

    const inGlobal = globalCartItemCodes.value.some(code => colorItemCodes.has(code));
    if (inGlobal) return true;

    return globalCatalogStore.cartItemsForCurrentTier.some(item => {
        const code = item.itemCode || item.ItemCode;
        if (code && colorItemCodes.has(code)) return true;
        const itemProductId = item.productId || item.ProductId;
        if (productId != null && itemProductId != null && String(itemProductId) !== String(productId)) {
            return false;
        }
        return mdsCartColorEquals(item, colorName);
    });
};

/** MDS: size chip is "in cart" when that size (+ selected color when present) is on BOM */
const isSizeInCart = (sizeVariant) => {
    if (!hasUserSelectedVariant.value && !isProductCatalog.value && !mdsCartCoversProduct.value) return false;

    const sizeName = sizeVariant?.sizeName || sizeVariant?.SizeName;
    if (!sizeName) return isItemInCart(sizeVariant?.itemCode || sizeVariant?.ItemCode);

    const productId = props.product?.productId || props.product?.ProductId;
    const colorFilter = selectedColor.value;
    const variants = props.product?.variants || [];

    const matchingCodes = new Set(
        variants
            .filter(v => {
                const vSize = v.sizeName || v.SizeName || '';
                const vAgility = v.AgilitySize || v.agilitySize || '';
                const sizeOk = vSize === sizeName || vAgility === sizeName
                    || vSize.toLowerCase() === sizeName.toLowerCase()
                    || vAgility.toLowerCase() === sizeName.toLowerCase();
                if (!sizeOk) return false;
                if (!colorFilter) return true;
                return (v.colorName || v.ColorName) === colorFilter;
            })
            .map(v => v.itemCode || v.ItemCode)
            .filter(Boolean)
    );

    const inGlobal = globalCartItemCodes.value.some(code => matchingCodes.has(code));
    if (inGlobal) return true;

    return globalCatalogStore.cartItemsForCurrentTier.some(item => {
        const code = item.itemCode || item.ItemCode;
        if (code && matchingCodes.has(code)) return true;

        const itemProductId = item.productId || item.ProductId;
        if (productId != null && itemProductId != null && String(itemProductId) !== String(productId)) {
            return false;
        }

        const itemSize = item.selectedSize || item.SelectedSize || item.Options?.size
            || item.selectedAgilitySize || item.AgilitySize || '';
        const itemColor = item.SelectedColor || item.selectedColor || item.Options?.color || '';

        if (itemSize) {
            const sizeOk = itemSize === sizeName || String(itemSize).toLowerCase() === String(sizeName).toLowerCase();
            if (!sizeOk) return false;
            if (!colorFilter) return true;
            return !itemColor || mdsCartColorEquals(item, colorFilter);
        }

        // Color-only cart rows must not checkmark every size for that color.
        // Size chips require itemCode (below) or selectedSize (above).

        if (code) {
            const cartVariant = variants.find(v => (v.itemCode || v.ItemCode) === code);
            if (!cartVariant) return false;
            if (colorFilter && (cartVariant.colorName || cartVariant.ColorName) !== colorFilter) return false;
            const vSize = cartVariant.sizeName || cartVariant.SizeName || '';
            return vSize === sizeName || String(vSize).toLowerCase() === String(sizeName).toLowerCase();
        }

        return false;
    });
};

const orderCatalogContainer = ref(null);
    const setSelectedProduct = () => {
        globalCatalogStore.existingScrollAmount = isProductCatalog.value ? window.scrollY : orderCatalogContainer.value.scrollTop;

        if (selectedVariant.value) {
            globalCatalogStore.selectedItemCode = selectedVariant.value.itemCode;
        }

        globalCatalogStore.setSelectedProduct(props.product.productId, props.product);
    }

function formatPrice(value) {
  const num = parseFloat(value); // Convert to number safely

  if (isNaN(num)) {
    return '0.00'; // fallback
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
const isPriceViewer = computed(() => globalCatalogStore.userRoles?.includes('PriceViewer') ?? false);
const isOrderingViewer = computed(() => globalCatalogStore.userRoles?.includes('OrderingViewer') ?? false);

// Add to template modal
const showModal = ref(false);
const orderTemplateStore = useOrdersPageStore();
const brands = computed(() => orderTemplateStore.brands);
const templates = computed(() => orderTemplateStore.orderTemplateData);
const brandsToShow = ref([])
const selectedBrand = ref('')
const selectedTemplates = ref([])
const isCheckAll = ref(false)
const showMoreBrands = ref(false)

const filteredTemplates = computed(() =>
  templates.value.filter(t => t.brand === selectedBrand.value)
);

const checkAllTemplates = () => {
    if(isCheckAll.value) {
        selectedTemplates.value = [...new Set([...selectedTemplates.value, ...filteredTemplates.value])]
    } else {
        selectedTemplates.value = selectedTemplates.value.filter(template => !filteredTemplates.value.some(ftemplate => ftemplate.id === template.id));
    }
}

const openAddToTemplateModal = async () => {
    if ( templates.value.length ) {
        selectedBrand.value = brands.value[0];
        selectedTemplates.value = [];
        brandsToShow.value = [...brands.value];
        showModal.value = true;

        await nextTick()
        updateVisibleItems()
        window.addEventListener('resize', updateVisibleItems)
    }
}
const loadingAddToTemplate = ref(false);

const addToTemplate = async () => {
    const totalSelectedTemplates = selectedTemplates.value.length;
    if ( totalSelectedTemplates ) {
        loadingAddToTemplate.value = true;
        for (let i = 0; i < totalSelectedTemplates; i++ ) {
            const selectedTemplate = selectedTemplates.value[i];
            await orderTemplateService.getOrderTemplateDetailsById(selectedTemplate.id)
                .then(async response => {
                    if (response && response.success) {
                        const template = response.data;
                        if ( template.Items.find(item => item.ItemCode === selectedVariant?.value?.itemCode) === undefined ) {
                            const templateOptions = JSON.parse(template.TemplateOptions);
                            const templateItem = {
                                Category: props.product.category,
                                ItemCode: selectedVariant?.value?.itemCode,
                                Quantity: templateOptions.QuantitySpecific ? /*item.Quantity*/ 0 || 0 : 0,
                                ItemOptions: JSON.stringify({
                                    WasteFactor: '', //item.WasteFactor,
                                    MeasurementFactor: '', //item.MeasurementFactor,
                                    DefaultUOM: uom.value,
                                    ItemCode: selectedVariant?.value?.itemCode, // ADDED FOR USE ON NEW TEMPLATE ORDERS
                                    IsPCItem: true, // ADDED FOR USE ON NEW TEMPLATE ORDERS
                                    Manufacturer: props.product.manufacturer, // FOR GROUPING NEW TEMPATE ORDERS
                                    ProductId: props.product.productId,
                                    ProductWebName: props.product.webName,
                                    Options: props.product.options || {}
                                }),
                                WebName: props.product.webName,
                                UserSort: 0
                            };
                            
                            template.Items.push(templateItem);
                            await orderTemplateService.updateOrderTemplate(template);
                        }
                    }
                })
                .catch(error => {
                    showError.value=true;
                    console.warn(error)
                })
        }
        showModal.value = false;
        loadingAddToTemplate.value = false
    }
}

const flexContainer = ref(null)
function updateVisibleItems() {
  if (!flexContainer.value) return

  const container = flexContainer.value
  const children = Array.from(container.children)
  let lastVisibleIndex = children.length - 1

  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    const rect = child.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    // If the child goes outside the container width, stop there
    if (rect.right > containerRect.right) {
      lastVisibleIndex = i - 1
      break
    }
  }
  lastVisibleIndex++;
  if ( lastVisibleIndex < brands.value.length ) {
    // Update visible items based on overflow detection
    brandsToShow.value = brands.value.slice(0, lastVisibleIndex)
    showMoreBrands.value = true;
  } else {
    showMoreBrands.value = false;
  }
}

const showAllBrands = ref(false);
const setSelectedBrandFromDropdown = brand => {
    selectedBrand.value = brand;
    showAllBrands.value = false;
}
const handleClickOutside = event => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        dropdownOpen.value = false;
    }
    if (!event.target.closest(".show-more-brands-dropdown")) {
        showAllBrands.value = false;
    }
    if (!event.target.closest(`.add-to-template-dropdown-${props.index}`)) {
        showAddToTemplateDropdown.value = false;
    }
}
watch(
    () => brands.value, 
    async () => {
        await nextTick()
        updateVisibleItems()
    }
)

watch(
    () => [selectedTemplates.value, selectedBrand.value],
    () => {
        const selectedTemplateInCurrentBrand = filteredTemplates.value.filter(template => selectedTemplates.value.some(stemplate => stemplate.id === template.id)).length;
        isCheckAll.value = selectedTemplateInCurrentBrand === filteredTemplates.value.length
    })
    
// External cart for BOM
const isProductCatalog = ref(true); // isProductCatalog or OrderCatalog

const resolveSelectedVariantForAdd = () => {
    if (selectedVariant.value?.itemCode || selectedVariant.value?.ItemCode) {
        return selectedVariant.value;
    }
    const variants = props.product?.variants || [];
    const color = selectedColor.value;
    const size = selectedSize.value;

    if (hasColorFeature.value && hasSizeFeature.value && color && size) {
        return variants.find(v =>
            (v.colorName || v.ColorName) === color &&
            (v.sizeName || v.SizeName) === size
        ) || null;
    }
    if (hasColorFeature.value && color && !hasSizeFeature.value) {
        return variants.find(v => (v.colorName || v.ColorName) === color) || null;
    }
    if (hasSizeFeature.value && size && !hasColorFeature.value) {
        return variants.find(v => (v.sizeName || v.SizeName) === size) || null;
    }
    return null;
};

const variantMatchesCurrentSelection = (variant) => {
    if (!variant) return false;
    const vColor = variant.colorName || variant.ColorName || '';
    const vSize = variant.sizeName || variant.SizeName || '';
    const vAgility = variant.AgilitySize || variant.agilitySize || '';
    if (selectedColor.value && vColor !== selectedColor.value) return false;
    if (hasSizeFeature.value && selectedSize.value) {
        const size = selectedSize.value;
        const sizeOk = vSize === size
            || vAgility === size
            || String(vSize).toLowerCase() === String(size).toLowerCase()
            || String(vAgility).toLowerCase() === String(size).toLowerCase();
        if (!sizeOk) return false;
    }
    return true;
};

// While adding an item into BOM we only add the selected variant as variants array
// So the added product will only have 1 variant always
const isItemAddedToBOM = computed(() => {
    const cartItemsForCurrentTier = globalCatalogStore.cartItemsForCurrentTier;

    if (isTemplateSingleton.value) {
        if (props.product.selectableFeatures && props.product.selectableFeatures.includes('N/A')) {
            const resolvedVariant = resolveSelectedVariantForAdd();
            const variantItemCode = resolvedVariant?.itemCode || resolvedVariant?.ItemCode;
            let codesToCheck = [variantItemCode];
            if (props.product?.productId === globalCatalogStore.selectedProduct?.productId) {
                codesToCheck.push(globalCatalogStore.selectedItemCode);
            }
            codesToCheck = codesToCheck.filter(Boolean);
            if (cartItemsForCurrentTier.length > 0) {
                return cartItemsForCurrentTier.some(product => {
                    const itemCode = product.itemCode || product.ItemCode;
                    return itemCode && codesToCheck.includes(itemCode);
                });
            }
            return false;
        }
    }

    if (!hasUserSelectedVariant.value && !mdsCartCoversProduct.value) return false;

    // MDS: Added must match the same signals as swatch checkmarks (color + size when required)
    if (hasColorFeature.value && !selectedColor.value) return false;
    if (hasSizeFeature.value && !selectedSize.value && availableSizes.value.length > 1) return false;

    const resolvedSku = resolveSelectedVariantForAdd()
    const resolvedCode = resolvedSku?.itemCode || resolvedSku?.ItemCode
    if (resolvedCode && cartItemsForCurrentTier.some(p => (p.itemCode || p.ItemCode) === resolvedCode)) {
        return true
    }

    if (hasColorFeature.value) {
        const colorVariant = availableColors.value.find(c =>
            (c.colorName || c.ColorName) === selectedColor.value
        ) || { colorName: selectedColor.value };
        if (!isColorInCart(colorVariant)) return false;
    }

    if (hasSizeFeature.value && selectedSize.value) {
        const sizeVariant = availableSizes.value.find(s =>
            (s.sizeName || s.SizeName) === selectedSize.value
        ) || { sizeName: selectedSize.value };
        if (!isSizeInCart(sizeVariant)) return false;
    }

    // Color-only product (or sole size already implied by color check)
    if (hasColorFeature.value) return true;

    // Size-only: fall back to resolved SKU
    const resolvedVariant = resolveSelectedVariantForAdd();
    const variantItemCode = resolvedVariant?.itemCode || resolvedVariant?.ItemCode;
    if (variantItemCode) {
        return cartItemsForCurrentTier.some(p => (p.itemCode || p.ItemCode) === variantItemCode);
    }
    return false;
});

const deleteItemFromBOM = () => {
    const resolvedVariant = resolveSelectedVariantForAdd();
    const delVariantCode = resolvedVariant?.itemCode
        || resolvedVariant?.ItemCode
        || selectedVariant.value?.itemCode
        || selectedVariant.value?.ItemCode;
    const delStoreCode = globalCatalogStore.selectedItemCode;
    const delCartItems = globalCatalogStore.cartItemsForCurrentTier;
    // Remove the code that actually matches a cart item (not just first truthy)
    let codeInCart = [delVariantCode, delStoreCode].find(code =>
        code && delCartItems.some(p => (p.itemCode || p.ItemCode) === code)
    );
    // MDS fallback: find cart row by productId + color/size when itemCode missing on selection
    if (!codeInCart) {
        const productId = props.product?.productId || props.product?.ProductId;
        const match = delCartItems.find(p => {
            const itemCode = p.itemCode || p.ItemCode;
            if (itemCode) {
                const cartVariant = (props.product?.variants || []).find(v =>
                    (v.itemCode || v.ItemCode) === itemCode);
                if (cartVariant && variantMatchesCurrentSelection(cartVariant)) return true;
            }
            const itemProductId = p.productId || p.ProductId;
            if (productId != null && itemProductId != null && String(itemProductId) !== String(productId)) {
                return false;
            }
            const itemColor = p.SelectedColor || p.selectedColor || p.Options?.color || '';
            return selectedColor.value && itemColor === selectedColor.value;
        });
        codeInCart = match?.itemCode || match?.ItemCode || null;
        if (match && !codeInCart) {
            globalCatalogStore.removeItemFromBOMForCurrentTier(match.id, 'id');
            if (isTemplateSingleton.value) {
                isItemTemplateSingletonAddedToBOM.value = globalCatalogStore.cartItemsForCurrentTier.length > 0;
            }
            return;
        }
    }
    const selectedCode = codeInCart || delStoreCode || delVariantCode;

    globalCatalogStore.removeItemFromBOMForCurrentTier(selectedCode, 'itemCode');
    
    // Update singleton flag - check if the cart still has items for current tier
    if (isTemplateSingleton.value) {
        const cartItemsForCurrentTier = globalCatalogStore.cartItemsForCurrentTier;
        isItemTemplateSingletonAddedToBOM.value = cartItemsForCurrentTier.length > 0;
    }
}

const addItemToBOM = () => {
    const resolvedVariant = resolveSelectedVariantForAdd();
    if (resolvedVariant && resolvedVariant !== selectedVariant.value) {
        setSelectedVariant(resolvedVariant);
    }

    const productItem = {...props.product};
    productItem.id = `product-${productItem.productId}-${Date.now()}`;
    productItem.itemCode = resolvedVariant?.itemCode || resolvedVariant?.ItemCode || null;
    productItem.ItemCode = productItem.itemCode;
    productItem.SelectedColor = resolvedVariant?.colorName || resolvedVariant?.ColorName || null;
    productItem.selectedColor = productItem.SelectedColor;
    productItem.selectedVariant = resolvedVariant || null;
    productItem.UOM = uom.value;
    productItem.addedFromCatalog = true;
    // Add tier ID for estimate flows (null for non-estimate flows)
    productItem.tierId = globalCatalogStore.currentTierId;
    productItem.SelectedColor = selectedColor.value || resolvedVariant?.colorName || resolvedVariant?.ColorName || null;
    productItem.selectedColor = productItem.SelectedColor;
    productItem.selectedSize = selectedSize.value || resolvedVariant?.sizeName || resolvedVariant?.SizeName || null;
    productItem.SelectedSize = productItem.selectedSize;
    // Prefer SKU from color+size when UI had color/size but selectedVariant was briefly null
    if (!productItem.itemCode && productItem.SelectedColor) {
        const matched = resolveSelectedVariantForAdd();
        if (matched) {
            productItem.itemCode = matched.itemCode || matched.ItemCode || null;
            productItem.ItemCode = productItem.itemCode;
            productItem.selectedVariant = matched;
        }
    }

    globalCatalogStore.addItemToExternalCartForBOM(productItem);
    globalCatalogStore.hasUserSelectedVariant = true;
    if (productItem.itemCode) {
        globalCatalogStore.selectedItemCode = productItem.itemCode;
    }
    isItemTemplateSingletonAddedToBOM.value = true;
}
// External cart for BOM ends

const isMobile = ref(false);
const checkIsMobile = () => isMobile.value = window.innerWidth < 767;

const normalizeItem = (i) => ({
    productId: i.productId ?? i.ProductId,
    itemCode: i.itemCode ?? i.ItemCode
});

const getAddedVariant = () => {
    const variants = props.product.variants || []
    const selectedItemCode = globalCatalogStore.selectedItemCode;
    const pid = props.product?.productId ?? props.product?.ProductId
    const cartItems = globalCatalogStore.cartItemsForCurrentTier
    const codeOnThisProductCart = (code) =>
        !!code && cartItems.some(i =>
            String(i.productId || i.ProductId) === String(pid) &&
            (i.itemCode || i.ItemCode) === code
        )

    if (selectedItemCode) {
        const fromStore = variants.find(v => v.itemCode === selectedItemCode) || null
        if (fromStore && (isProductCatalog.value || codeOnThisProductCart(selectedItemCode))) {
            return fromStore
        }
    }

    const addedItemCode =
            normalizeItem(
            globalCatalogStore.globalCartItems.find(
                i => normalizeItem(i).productId === props.product.productId
            ) || {}
        ).itemCode ||
        normalizeItem(
            cartItems.find(
                i => normalizeItem(i).productId === props.product.productId
            ) || {}
        ).itemCode;

    if (!addedItemCode) return null;

    return variants.find(
        v => v.itemCode === addedItemCode
    );
};

const hideOptions = ref(false)
onMounted(() => {
    isProductCatalog.value = window.location.pathname === "/products";
    
    // Adjusting scroll amount
    let scrollTop = 0;
    orderCatalogContainer.value = document.getElementById('product-catalog-container')
    if ( globalCatalogStore.componentToShow === "Products") {
        scrollTop = globalCatalogStore.existingScrollAmount ?? 0;
    }
    const container = isProductCatalog.value ? window : orderCatalogContainer.value;
    if ( container ) {
        container.scrollTo({
            top: scrollTop,
            left: 0,
            behavior: "smooth",
        });
    }

    const incomingColor = props.product?.selectedColor || props.product?.SelectedColor || ''
    const incomingSize = props.product?.selectedSize || props.product?.SelectedSize || ''

    let addedVariant = getAddedVariant();
    if (incomingColor || incomingSize) {
        const matchIncoming = (props.product.variants || []).find(v => {
            const colorOk = !incomingColor || (v.colorName || v.ColorName) === incomingColor
            if (!colorOk) return false
            if (!incomingSize) return true
            const vSize = v.sizeName || v.SizeName || ''
            const vAgility = v.AgilitySize || v.agilitySize || ''
            const s = String(incomingSize).toLowerCase()
            return vSize.toLowerCase() === s || vAgility.toLowerCase() === s
        })
        if (matchIncoming) addedVariant = matchIncoming
    }

    if (addedVariant) {
        setSelectedVariant(addedVariant);
    } else if (props.product.variants?.[0]) {
        setSelectedVariant(props.product.variants[0]);
    }

    checkIsMobile();
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('resize', checkIsMobile);

    if(props?.product.selectableFeatures[0]==='N/A'){
        hideOptions.value = true
    }

    if (incomingColor || incomingSize) {
        selectedColor.value = incomingColor
        selectedSize.value = incomingSize
            || (availableSizes.value.length === 1
                ? availableSizes.value.find(s => !s.disabled)?.sizeName
                : (addedVariant?.sizeName || addedVariant?.SizeName || null))
        globalCatalogStore.hasUserSelectedVariant = true
    } else {
        selectedSize.value =
            availableSizes.value.length === 1
                ? availableSizes.value.find(s => !s.disabled)?.sizeName
                : selectedVariant.value?.sizeName ?? null;

        selectedColor.value =
            availableColors.value.length === 1
                ? availableColors.value.find(c => !c.disabled)?.colorName
                : selectedVariant.value?.colorName ?? null;
    }
})

const isProductDetail = computed(() => componentToShow.value === 'ProductDetail');

const shouldFetchPI = computed(() =>
  isProductDetail.value && !!selectedVariant.value?.itemCode
);

// Single unified watcher: fetches pricing + inventory when entering ProductDetail
// with a variant, or when branch/shipTo changes while already in ProductDetail.
// Replaces the previous two separate watchers that could fire simultaneously
// and cause duplicate pricing/inventory requests.
let lastFetchKey = '';
watch(
  () => [shouldFetchPI.value, branchCode.value, selectedShipToId.value, selectedVariant.value?.itemCode],
  async ([isPI, branch, shipTo, itemCode]) => {
    if (!isPI || !itemCode) return;
    const fetchKey = `${branch}|${shipTo}|${itemCode}`;
    if (fetchKey === lastFetchKey) return;
    lastFetchKey = fetchKey;
    await getPricing();
    await getProductInventoryLevel();
  }
);

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('resize', checkIsMobile);
})
const formatDateForInput = (dateString) => {
    return dateString ? moment(dateString).format("MMM D YYYY") : null;
};
</script>
